import React from 'react'
import { ThemeBuilderEmbed, ThemeBuilderConfig, MediaServiceConfig } from '@iws/theme-builder'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { BusinessModel } from '@/http/models/business/Business'
import { CategoryModel } from '@/http/models/ProductCategory/ProductCategory'
import { ProductModel } from '@/http/models/Product/Product'
import { getFileUrl } from '@/clients/http'
import { MediaModel as MediaApi } from '@/http/models/media/Media'
import { configService } from '@/services'
import { useBusiness } from '@/contexts/BusinessContext'
import { mintPreviewToken, revalidateStore, storefrontOrigin } from '@/lib/storefront'
import { CAMPAIGN_TEMPLATES_KEY } from '@/lib/campaign-templates'
import { useSearchParams } from 'react-router-dom'
import { AssistantModel } from '@/http/models/assistant/Assistant'
import { setActiveProposal } from '@/lib/assistantAudit'
import { useAssistant } from '@/components/assistant/AssistantProvider'
import { ReviewBanner } from '@/components/assistant/ReviewBanner'
import { useReviewStamp } from '@/components/assistant/useReviewStamp'
import { AssistantProposal } from '@/types/assistant'

const mediaService: MediaServiceConfig = {
  getRootFolders: async () => {
    const response = await MediaApi.getRootFolders({ query: { page: 1, length: 100 } })
    return response.data?.result ?? []
  },

  getFolder: async (id: string) => {
    const response = await MediaApi.getFolder({ id })
    return response.data?.result ?? { _id: id, name: 'Folder', folders: [], files: [] }
  },

  uploadFile: async (file: File, folderId?: string | null) => {
    const formData = new FormData()
    if (folderId) formData.append('folder_id', folderId)
    formData.append('file', file)
    formData.append('name', file.name)
    const isImage = file.type.startsWith('image/')
    const response = isImage
      ? await MediaApi.uploadImage({ body: formData })
      : await MediaApi.uploadDocument({ body: formData })
    return response.data?.result
  },

  deleteFile: async (id: string) => {
    await MediaApi.deleteMedia({ id })
  },

  deleteFolder: async (id: string) => {
    await MediaApi.deleteFolder({ id })
  },

  createFolder: async (name: string, parentId?: string | null) => {
    const response = await MediaApi.createFolder({
      body: { name, ...(parentId ? { parent_id: parentId } : {}) },
    })
    return response.data?.result
  },
}

export const ThemeBuilderPage = () => {
  const { refreshFeatureFlags } = useFeatureFlags()
  const { profile } = useBusiness()
  const origin = storefrontOrigin(profile?.custom_customer_domain || profile?.platform_customer_domain)

  // The assistant opens the editor on a page/section/panel (?page, ?section, ?panel) or with prepared theme edits
  // (?proposal): they load as unpublished changes, and publishing them is the merchant's normal Publish.
  const [params, setParams] = useSearchParams()
  const { markProposal } = useAssistant()
  const proposalUid = params.get('proposal')
  const stamp = useReviewStamp()
  const [proposal, setProposal] = React.useState<Extract<AssistantProposal, { kind: 'theme_change' }> | null>(null)
  React.useEffect(() => {
    if (!proposalUid) return
    AssistantModel.getProposal(proposalUid).then((res) => {
      const p = res.success ? res.data?.result : null
      if (p?.kind === 'theme_change' && p.status === 'pending') setProposal((current) => (current?.proposal_uid === p.proposal_uid ? current : p))
      else setParams({}, { replace: true })
    })
  }, [proposalUid, setParams, stamp])
  React.useEffect(() => {
    if (!proposal) return
    setActiveProposal(proposal.proposal_uid)
    return () => setActiveProposal(null)
  }, [proposal])
  const finishProposal = (status: 'published' | 'discarded') => {
    if (!proposal) return
    markProposal(proposal.proposal_uid, status)
    setProposal(null)
    setParams({}, { replace: true })
  }
  const panel = params.get('panel')

  const config: ThemeBuilderConfig = {
    // Campaign templates share theme_settings but belong to the campaign builder: the theme editor
    // never sees them, and its saves keep whatever is stored now.
    fetchSettings: async () => {
      const response = await BusinessModel.getBusinessProfile()
      const settings = response.data?.result?.theme_settings as Record<string, unknown> | undefined
      if (!settings) return null
      const { [CAMPAIGN_TEMPLATES_KEY]: _templates, ...theme } = settings
      return theme
    },

    saveSettings: async (settings) => {
      const latest = await BusinessModel.getBusinessProfile()
      const templates = (latest.data?.result?.theme_settings as Record<string, unknown> | undefined)?.[CAMPAIGN_TEMPLATES_KEY]
      const response = await BusinessModel.updateBusinessProfile({
        body: { theme_settings: templates === undefined ? settings : { ...settings, [CAMPAIGN_TEMPLATES_KEY]: templates } },
      })
      if (!response.success) throw new Error('Failed to save theme settings')
    },

    fetchCategories: async () => {
      const response = await CategoryModel.getList({ query: { length: 100 } })
      const result = response.data?.result ?? []
      return result.map((c: { _id: string; name: string }) => ({ _id: c._id, name: c.name }))
    },

    searchProducts: async (query: string) => {
      const response = await ProductModel.getList({ query: { search: query || undefined, length: 20 } })
      return (response.data?.result ?? []).map((p: { _id: string; name: string; thumbnail?: { path?: string } | null }) => ({
        _id: p._id,
        name: p.name,
        thumbnail: p.thumbnail?.path ?? null,
      }))
    },

    resolveImageUrl: (path) => getFileUrl(path),

    mediaService,

    onSaved: () => {
      refreshFeatureFlags()
      finishProposal('published')
    },
    initialView: {
      page: params.get('page') ?? undefined,
      sectionId: params.get('section') ?? undefined,
      panel: panel === 'sections' || panel === 'settings' || panel === 'setup' ? panel : undefined,
    },
    pendingEdits: proposal ? { key: proposal.proposal_uid, ops: proposal.payload.ops } : undefined,

    // Drafts and the local version history are kept per store.
    storeKey: configService.getConfig()?.apiConfig?.clientHost,

    // Every section works in every theme on the Nuxt storefront; stores still on the Next storefront only
    // get their theme's own sections. Switch on per environment once its stores run on Nuxt.
    sectionLibrary: import.meta.env.VITE_THEME_ALL_SECTIONS === 'true' ? 'all' : 'theme',

    preview: origin
      ? {
          storefrontOrigin: origin,
          getToken: () => mintPreviewToken(origin, { scope: 'theme' }),
          revalidate: () => revalidateStore(origin),
        }
      : undefined,
  }

  return (
    <>
      <ThemeBuilderEmbed config={config} />
      {proposal && (
        <div className="pointer-events-none fixed inset-x-0 top-14 z-50 flex justify-center px-4">
          {/* The editor's own Discard and undo remove the edits; Publish marks the proposal saved. */}
          <ReviewBanner mode="publish" className="pointer-events-auto shadow-dropdown" />
        </div>
      )}
    </>
  )
}
